import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import ReactHighcharts from 'react-highcharts';
import Moment from 'moment';
import { extendMoment } from 'moment-range';
import numeral from 'numeral';
import { Dropdown, MenuItem } from 'react-bootstrap';
import { groupBy, map, orderBy, first, findIndex, reduce, sumBy, each, capitalize, find } from 'lodash';

import PeriodDropdown from './period-dropdown';
import PeriodForm from './period-form';
import { getColorFromKey } from '../../../../util/helpers';
import { getHasNoDecisionSetup } from '../../../../components/search-form/search-form-reducer';
import { format as formatMoney } from '../../../../components/money-display';
import { getUserCurrentOrganization } from '../../../Auth/AuthReducer';
import { getIsFontSizeBig } from '../../../App/AppReducer';

// Import Selectors
import { getOpportunities, getLoaded, getLoading } from '../../AnalysisReducer';

const moment = extendMoment(Moment);

const USER_PREFERENCE_RATE_TYPE = 'outcomes-rate-graph-rate-type';
const USER_PREFERENCE_REPORT_TYPE = 'outcomes-rate-graph-report-type';
const USER_PREFERENCE_PERIOD_TYPE = 'outcomes-rate-graph-period-type';
const USER_PREFERENCE_PERIOD_DAYS = 'outcomes-rate-graph-period-days';

class RateGraph extends Component {
  constructor(props) {
    super(props);
    this.state = {
      rateType: 'Won', // 'Won' 'Lost', 'No Decision',
      reportType: 'amount', // 'amount', 'count'
      period: 'monthly',
      days: null,
      modalOpen: false,
    };
    this.setRateType = this.setRateType.bind(this);
    this.setReportType = this.setReportType.bind(this);
    this.handlePeriodChange = this.handlePeriodChange.bind(this);
    this.handleCustomPeriodSelect = this.handleCustomPeriodSelect.bind(this);
    this.handlePeriodDayChange = this.handlePeriodDayChange.bind(this);
    this.afterRender = this.afterRender.bind(this);
  }

  componentWillMount() {
    const { userPreferences } = this.context;
    const rateType = userPreferences[USER_PREFERENCE_RATE_TYPE] || 'Won';
    const reportType = userPreferences[USER_PREFERENCE_REPORT_TYPE] || 'amount';
    const period = userPreferences[USER_PREFERENCE_PERIOD_TYPE] || 'monthly';
    const days = userPreferences[USER_PREFERENCE_PERIOD_DAYS] || 1;
    this.setState({
      rateType, reportType, period, days,
    });
  }


  getRateTypeLable(rateType) {
    if (rateType === 'Won') {
      return 'Win Rate';
    } else if (rateType === 'Lost') {
      return 'Loss Rate';
    }
    return `${rateType} Rate`;
  }

  setRateType(rateType) {
    this.setState({ rateType });
    this.context.updateUserPreference(USER_PREFERENCE_RATE_TYPE, rateType);
  }
  setReportType(reportType) {
    this.setState({ reportType });
    this.context.updateUserPreference(USER_PREFERENCE_REPORT_TYPE, reportType);
  }

  handlePeriodChange(period) {
    this.setState({ period, days: null });
    this.context.updateUserPreference(USER_PREFERENCE_PERIOD_TYPE, period);
    this.context.updateUserPreference(USER_PREFERENCE_PERIOD_DAYS, null);
  }

  handleCustomPeriodSelect() {
    this.setState({ modalOpen: true });
  }

  handlePeriodDayChange(days) {
    this.setState({ days, period: 'days' });
    this.context.updateUserPreference(USER_PREFERENCE_PERIOD_TYPE, 'days');
    this.context.updateUserPreference(USER_PREFERENCE_PERIOD_DAYS, days);
  }

  afterRender(chart) {
    const ext = chart.yAxis[0].getExtremes();
    const dMax = Math.abs(ext.dataMax);
    const dMin = Math.abs(ext.dataMin);
    const dExt = dMax >= dMin ? dMax : dMin;
    const min = 0 - dExt;
    chart.yAxis[0].setExtremes(min, dExt);
  }

  renderGraph() {
    const {
      loaded, loading, opportunities, hasNoDecisionSetup, organization, closeDateRange,
      isFontSizeBig,
    } = this.props;
    if (!loaded || loading || opportunities.length === 0) {
      return null;
    }
    let suffix = '';
    let symbol = '';
    if (organization) {
      suffix = organization.currency_suffix;
      symbol = organization.currency_symbol;
    }
    const {
      period, reportType, rateType, days,
    } = this.state;
    const rateLabel = this.getRateTypeLable(rateType);
    // group opportunities
    let format = null;
    if (period === 'monthly') {
      format = 'YYYYMM';
    } else if (period === 'quarterly') {
      format = 'YYYYQ';
    } else if (period === 'annual') {
      format = 'YYYY';
    }

    const inputOpportunities = orderBy(opportunities.map((opportunity) => {
      opportunity.close_date = moment(opportunity.close_date, 'YYYY-MM-DD').startOf('day');
      return opportunity;
    }), ['close_date', 'asc']);


    let groupedOpportunities = {};
    if (closeDateRange && closeDateRange.length > 1 && closeDateRange[0] && closeDateRange[1]) {
      let mRange = moment.range(closeDateRange[0], closeDateRange[1]);
      if (period === 'monthly') {
        mRange = moment.range(moment(closeDateRange[0]).startOf('month'), moment(closeDateRange[1]).endOf('month'));
      } else if (period === 'quarterly') {
        mRange = moment.range(moment(closeDateRange[0]).startOf('quarter'), moment(closeDateRange[1]).endOf('quarter'));
      } else if (period === 'annual') {
        mRange = moment.range(moment(closeDateRange[0]).startOf('year'), moment(closeDateRange[1]).endOf('year'));
      }
      let rIt = 'days';
      let step = days;
      if (period === 'monthly') {
        rIt = 'months';
        step = 1;
      } else if (period === 'quarterly') {
        rIt = 'quarters';
        step = 1;
      } else if (period === 'annual') {
        rIt = 'years';
        step = 1;
      }

      const labelRange = Array.from(mRange.by(rIt, { exclusive: false, step }));
      const xLabels = labelRange.map((m, i) => moment.range(m, m.clone().add(step, rIt).subtract(i === labelRange.length - 1 ? 0 : 1, 'days')));
      groupedOpportunities = reduce(xLabels, (items, l) => {
        const k = l.start.format('YYYY-MM-DD');
        items[k] = [];
        return items;
      }, {});
      each(inputOpportunities, (opportunity) => {
        if (opportunity.close_date) {
          const idx = findIndex(xLabels, xl => xl.contains(opportunity.close_date));
          if (idx >= 0) {
            groupedOpportunities[xLabels[idx].start.format('YYYY-MM-DD')].push(opportunity);
          }
        }
      });
    } else if (format) {
      groupedOpportunities = format ? groupBy(inputOpportunities, opportunity => (opportunity.close_date ? opportunity.close_date.format(format) : null)) : {};
    } else if (period === 'days' && days !== null && days !== 0) {
      const minDateOpportunities = inputOpportunities[0];
      const start = minDateOpportunities.close_date;
      groupedOpportunities = groupBy(inputOpportunities, (opportunity) => {
        const d = opportunity.close_date.diff(start, 'days');
        return Math.floor(d / days);
      });
    }

    groupedOpportunities = orderBy(map(groupedOpportunities, (groupedOpportunity, gPeriod) => {
      let amount = 0;
      let count = 0;
      const rates = {};
      const items = reduce(groupBy(groupedOpportunity, opportunity => opportunity.stage), (result, value, stage) => {
        result[stage] = {
          amount: sumBy(value, o => o.amount || 0),
          count: value.length,
          ids: map(value, 'opportunity_id'),
        };
        amount += result[stage].amount;
        count += result[stage].count;
        rates[stage] = 0;
        return result;
      }, {});
      const closeDate = groupedOpportunity.length ? first(groupedOpportunity).close_date : moment(gPeriod, 'YYYY-MM-DD');
      const result = {
        period: gPeriod,
        items,
        amount,
        count,
        rates: reduce(rates, (obj, rate, stage) => {
          if (reportType === 'amount') {
            obj[stage] = amount !== 0 ? (items[stage].amount / amount) * 100.00 : 0;
          } else if (reportType === 'count') {
            obj[stage] = count !== 0 ? (items[stage].count / count) * 100.00 : 0;
          }
          return obj;
        }, {}),
        average: count !== 0 ? amount / count : 0,
        closeDate,
      };
      return result;
    }), ['period', 'asc']);

    const series = [
      {
        name: 'Won', color: getColorFromKey('won'), data: [],
      },
      {
        name: 'Lost', color: getColorFromKey('lost'), data: [],
      },
    ];
    if (hasNoDecisionSetup) {
      series.push({
        name: 'No Decision', color: getColorFromKey('noDecision'), data: [],
      });
    }
    const rateSeries = {
      name: rateLabel,
      color: '#0c7cd5',
      data: [],
      yAxis: 1,
      type: 'line',
      marker: {
        symbol: 'circle',
      },
    };

    const avgSeries = {
      name: 'Avg deal size',
      color: '#ffffff',
      lineColor: 'transparent',
      marker: {
        fillColor: 'transparent', // make the line markers invisible
        lineWidth: 0,
        radius: 0,
        lineColor: 'transparent',
        states: {
          hover: {
            enabled: false, // prevent the highlight circle from showing when hovered
          },
        },
      },
      data: [],
      type: 'line',
      showInLegend: false,
    };
    const categories = map(groupedOpportunities, (groupedOpportunity) => {
      let f = 'll';
      if (period === 'monthly') {
        f = 'MMM YYYY';
      } else if (period === 'quarterly') {
        f = '[Q]Q YYYY';
      } else if (period === 'annual') {
        f = 'YYYY';
      }
      const formatedAverage = formatMoney(groupedOpportunity.average, true, 1, symbol, suffix);
      each(series, (s) => {
        const stage = s.name;
        const modifier = stage === 'Won' ? 1 : -1;
        let y = 0;
        if (groupedOpportunity.items[stage]) {
          y = modifier * (groupedOpportunity.items[stage][reportType] || 0);
        }
        const absY = Math.abs(y);
        let formatedY = 0;
        if (absY > 0) {
          if (reportType === 'amount') {
            formatedY = formatMoney(absY, true, 1, symbol, suffix);
          } else {
            formatedY = numeral(absY).format('0');
          }
        }
        s.data.push({
          y,
          formatedY,
          opportunityIds: groupedOpportunity.items[stage] ? groupedOpportunity.items[stage].ids : [],
          events: {
            click: (event) => {
              this.props.onBarSelect(event.point.options.opportunityIds);
            },
          },
        });
      });
      const rateY = groupedOpportunity.rates[rateType] || 0; // cheat
      rateSeries.data.push({ y: rateY, formatedY: `${numeral(rateY).format('0.0')}%` });
      avgSeries.data.push({ y: 0, formatedY: formatedAverage });
      return groupedOpportunity.closeDate.format(f);
    });
    series.push(rateSeries);
    series.unshift(avgSeries);
    let yAxisTitle = capitalize(reportType);
    if (reportType === 'amount' && suffix && suffix.length) {
      yAxisTitle += ` (${suffix})`;
    }
    const config = {
      chart: {
        plotBackgroundColor: null,
        plotBorderWidth: null,
        plotShadow: false,
        backgroundColor: 'rgba(0, 0, 0, 0)',
        type: 'column',
        animation: {
          duration: 1000,
        },
      },
      credits: {
        enabled: false,
      },
      title: {
        text: null,
      },
      xAxis: {
        categories,
        crosshair: true,
        labels: {
          style: {
            fontSize: isFontSizeBig ? '16px' : null,
          },
        },
      },
      tooltip: {
        headerFormat: `<span style="font-weight: bold; ${isFontSizeBig ? 'font-size:16px' : 'font-size:12px'}">{point.key}</span>
          <table class="table table-condensed" style="${isFontSizeBig ? 'font-size:16px' : 'font-size:12px'}; margin-top:1em; margin-bottom: 0;">`,
        pointFormat: `<tr>
          <td style="color:{series.color};">{series.name}:&nbsp;</td>
          <td class="text-right"><b>{point.formatedY}</b></td>
        </tr>`,
        footerFormat: '</table>',
        shared: true,
        useHTML: true,
        backgroundColor: 'rgba(0,0,0, 0.75)',
        borderColor: 'rgba(0,0,0, 0.95)',
        style: {
          color: 'rgba(255, 255, 255, 0.75)',
        },
      },
      legend: {
        itemStyle: {
          fontSize: isFontSizeBig ? '16px' : null,
        },
      },
      yAxis: [
        {
          title: {
            text: yAxisTitle,
            style: {
              fontSize: isFontSizeBig ? '16px' : null,
              fontWeight: 'bold',
            },
          },
          tickAmount: 9,
          labels: {
            formatter: function f() {
              let v = 0;
              if (this.value !== 0) {
                if (reportType === 'amount') {
                  v = formatMoney(this.value, true, 1, symbol);
                } else {
                  v = numeral(this.value).format('0');
                }
              }
              return v;
            },
            style: {
              fontSize: isFontSizeBig ? '16px' : null,
            },
          },
          alternateGridColor: 'rgba(66,66,66,0.05)',
          lineWidth: 0,
          plotLines: [{
            value: 0,
            dashStyle: 'dashed',
            color: 'rgba(0, 0, 0, 0.5)',
            width: 2,
            label: {
              text: null,
            },
          }],
        },
        {
          gridLineWidth: 0,
          max: 100,
          min: 0,
          allowDecimals: false,
          title: {
            text: rateLabel,
            style: {
              color: '#0c7cd5',
              fontSize: isFontSizeBig ? '16px' : null,
              fontWeight: 'bold',
            },
          },
          labels: {
            formatter: function f() {
              return `${this.value}%`;
            },
            style: {
              color: '#0c7cd5',
              fontSize: isFontSizeBig ? '16px' : null,
            },
          },
          opposite: true,
        },
      ],
      plotOptions: {
        series: {
          stacking: 'normal',
          marker: {
            fillColor: '#ffffff',
            lineWidth: 2,
            radius: 5,
            lineColor: null, // inherit from series
          },
        },
      },
      series,
    };

    return (
      <div>
        <ReactHighcharts config={config} isPureConfig callback={this.afterRender} />
      </div>
    );
  }

  render() {
    const {
      loaded, loading, opportunities, hasNoDecisionSetup,
    } = this.props;
    if (!loaded || loading || opportunities.length === 0) {
      return null;
    }
    const {
      rateType, reportType, period, modalOpen, days,
    } = this.state;
    const stages = [
      { value: 'Won', label: 'Win Rate' },
      { value: 'Lost', label: 'Loss Rate' },
    ];
    if (hasNoDecisionSetup) {
      stages.push({ value: 'No Decision', label: 'No Decision Rate' });
    }
    const activeStage = find(stages, { value: rateType }) || { label: 'Rate' };

    const reportTypes = [
      { value: 'amount', label: 'Amount' },
      { value: 'count', label: 'Count' },
    ];
    const activeReportType = find(reportTypes, { value: reportType }) || { label: 'Amount' };


    return (
      <div>
        <div className="animated fadeIn">
          <div>
            <h5 className="sub-header-with-controls">
              View&nbsp;
              <Dropdown
                id="stage-dropdown"
              >
                <Dropdown.Toggle bsSize="small">
                  {activeStage.label}
                </Dropdown.Toggle>
                <Dropdown.Menu>
                  {stages.map(s => (
                    <MenuItem
                      key={s.value}
                      active={s.value === rateType}
                      onSelect={() => { this.setRateType(s.value); }}
                    >
                      {s.label}
                    </MenuItem>
                    ))}
                </Dropdown.Menu>
              </Dropdown>
              &nbsp;
              <span>over time by</span>
              &nbsp;
              <Dropdown
                id="report-type-dropdown"
              >
                <Dropdown.Toggle bsSize="small">
                  {activeReportType.label}
                </Dropdown.Toggle>
                <Dropdown.Menu>
                  {reportTypes.map(s => (
                    <MenuItem
                      key={s.value}
                      active={s.value === reportType}
                      onSelect={() => { this.setReportType(s.value); }}
                    >
                      {s.label}
                    </MenuItem>
                    ))}
                </Dropdown.Menu>
              </Dropdown>
              &nbsp;
              <span>and compare</span>
              &nbsp;
              <PeriodDropdown
                period={period}
                days={days}
                onChange={this.handlePeriodChange}
                onCustomPeriodSelect={this.handleCustomPeriodSelect}
              />
              &nbsp;
              periods
            </h5>
          </div>
          {this.renderGraph()}
        </div>
        <PeriodForm
          maxValue={opportunities.length}
          modalOpen={modalOpen}
          onDayChange={this.handlePeriodDayChange}
          onHide={() => { this.setState({ modalOpen: false }); }}
        />
      </div>
    );
  }
}

RateGraph.propTypes = {
  opportunities: PropTypes.arrayOf(PropTypes.shape({
    opportunity_id: PropTypes.string.isRequired,
  })).isRequired,
  organization: PropTypes.shape({
    organization_id: PropTypes.string.isRequired,
  }),
  loaded: PropTypes.bool.isRequired,
  loading: PropTypes.bool.isRequired,
  hasNoDecisionSetup: PropTypes.bool.isRequired,
  isFontSizeBig: PropTypes.bool.isRequired,
  onBarSelect: PropTypes.func,
  closeDateRange: PropTypes.arrayOf(PropTypes.instanceOf(moment)),
};

RateGraph.defaultProps = {
  organization: null,
  closeDateRange: [],
  onBarSelect: () => {},
};

RateGraph.contextTypes = {
  updateUserPreference: PropTypes.func.isRequired,
  userPreferences: PropTypes.object.isRequired,
};

// Retrieve data from store as props
function mapStateToProps(state) {
  return {
    opportunities: getOpportunities(state),
    organization: getUserCurrentOrganization(state),
    loaded: getLoaded(state),
    loading: getLoading(state),
    hasNoDecisionSetup: getHasNoDecisionSetup(state),
    isFontSizeBig: getIsFontSizeBig(state),
  };
}

export default connect(mapStateToProps)(RateGraph);
